/*
 * The client's past visits, for the services step's "↻ AGAIN" (SF 41, N13;
 * utils/lastVisit.ts) and the ↻ tags.
 *
 * At a location, one small read: the CMS's last-visit read
 * (`/api/appointment/last-visits?storeId=`, CMS G21, mpgadmin G117), per pet
 * its last three visits there that happened, with each line's service and
 * option ids. A few hundred bytes from one API call (13 Oct 2026, "Faster
 * loads, part 2"). Before it, and still whenever it isn't there or fails, one
 * read of the client's own bookings (the CMS's by-client: their whole history,
 * which the CMS builds from three API calls), mapped to the little the last
 * visit needs (`visitsFromBookings`). A caller that names no location gets
 * the one being booked; by-client is also the read when none is known. Per
 * pet up to three visits, newest first: AGAIN shows the latest, and the ↻ tags
 * ("{pet}'s usual", on 2 of the last 3; "Last visit: Medium") read all three.
 * Either way the answer is the same `VisitLike[]` (one visit per pet and
 * time, with its status), so what reads visits doesn't know which read it
 * came from.
 *
 * Until the API and the CMS carry the new read (a 404 before the API's
 * release, a 400 before the CMS's, where `/appointment/:id` takes
 * "last-visits" for an id), a route that isn't there is remembered for ten
 * minutes, so the step doesn't ask it again on every visit. It is asked with
 * a plain fetch: nothing it answers ever signs the client out; the bookings
 * read after it keeps today's handling of an ended session.
 *
 * It used to go through My bookings' full transform, which after the bookings
 * read the client record, the salon's locations and, one after the other, both
 * menus of every location in the history: four or more round trips in a row
 * before AGAIN could show, and it then pushed the whole list down (Lena, 13 Oct
 * 2026: "some loading at the start is slow").
 *
 * Held in memory only, for the signed-in record (its id and token), for the
 * visit, so a pet whose last visit is known shows it at once when the step
 * opens again; dropped at sign-out (G1). The bookings answer every location;
 * the last-visit read one. A record switched by a code (G13) is a new key,
 * read afresh. A failed read is "no visits" for now and isn't kept, so the next
 * visit to the step tries again.
 */
import { CMS_BASE_URL } from '../config/api.config';
import { getCMSClientId, getCMSJWT, makeAuthenticatedRequest } from './auth.service';
import { visitsFromBookings, visitsFromLastVisits, type VisitLike } from '../utils/lastVisit';

/** A visit happens at most a few times a day; half an hour is plenty fresh for "last time". */
const FRESH_MS = 30 * 60 * 1000;
/** How long a last-visit read that isn't there yet is left alone. */
const ROUTE_MISSING_MS = 10 * 60 * 1000;
/** What the last-visit read answers where it isn't there yet (see above); anything else is tried again next time. */
const NOT_THERE = new Set([400, 403, 404, 405, 501]);

/** The location the visits are for, or every location (the bookings). */
type Scope = number | 'all';

let kept: { key: string; scope: Scope; at: number; visits: VisitLike[] } | null = null;
let inflight: { key: string; want: Scope; promise: Promise<VisitLike[]> } | null = null;
let routeMissingUntil = 0;

const keyNow = (): string | null => {
  const jwt = getCMSJWT();
  return jwt ? `${getCMSClientId() ?? ''}:${jwt}` : null;
};

/** The location being booked, as the booking wrapper keeps it, for a caller that doesn't name one. */
const chosenStoreId = (): unknown => {
  try {
    return JSON.parse(localStorage.getItem('selectedStore') || 'null')?.id;
  } catch {
    return null;
  }
};

/**
 * The location to read for: the one named, else the one being booked (every
 * caller is the services step, so a caller that doesn't say, such as phase
 * 9c's visit list, shares the same small read), else every location. An id
 * kept as text reads as its number.
 */
const scopeOf = (storeId?: number | string | null): Scope => {
  const id = Number(storeId ?? chosenStoreId());
  return Number.isInteger(id) && id > 0 ? id : 'all';
};
const covers = (have: Scope, want: Scope) => have === 'all' || have === want;

/**
 * The signed-in record's visits when already read, for the location (or every
 * location being booked, with none); undefined while they aren't.
 */
export const peekVisits = (storeId?: number | string | null): VisitLike[] | undefined => {
  const key = keyNow();
  if (!key || kept?.key !== key || Date.now() - kept.at >= FRESH_MS || !covers(kept.scope, scopeOf(storeId))) return undefined;
  return kept.visits;
};

/** The last-visit read for the location; null when it isn't there yet, failed, or answered something else. */
const readLastVisits = async (storeId: number): Promise<VisitLike[] | null> => {
  const jwt = getCMSJWT();
  if (!jwt) return null;
  try {
    const response = await fetch(`${CMS_BASE_URL}/api/appointment/last-visits?storeId=${storeId}`, {
      headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) {
      if (NOT_THERE.has(response.status)) routeMissingUntil = Date.now() + ROUTE_MISSING_MS;
      return null;
    }
    return visitsFromLastVisits(await response.json(), storeId);
  } catch {
    return null;
  }
};

/** The client's bookings, every location; null when the read failed. */
const readBookings = async (): Promise<VisitLike[] | null> => {
  const response = await makeAuthenticatedRequest(`${CMS_BASE_URL}/api/appointment/by-client`, { method: 'GET' });
  if (!response.ok) return null;
  return visitsFromBookings(await response.json());
};

/**
 * The signed-in record's visits, for the location when given: from memory,
 * the read under way, or one read (the last-visit read, else the bookings).
 * Never throws.
 */
export const readVisits = (storeId?: number | string | null): Promise<VisitLike[]> => {
  const key = keyNow();
  if (!key) return Promise.resolve([]);
  const want = scopeOf(storeId);
  const hit = peekVisits(storeId);
  if (hit) return Promise.resolve(hit);
  if (inflight?.key === key && inflight.want === want) return inflight.promise;
  const promise = (async (): Promise<VisitLike[]> => {
    try {
      let scope: Scope = 'all';
      let visits = want !== 'all' && Date.now() >= routeMissingUntil ? await readLastVisits(want) : null;
      if (visits) scope = want;
      else visits = await readBookings();
      if (!visits) return [];
      if (keyNow() === key) kept = { key, scope, at: Date.now(), visits };
      return visits;
    } catch {
      // No AGAIN row this time; the next visit to the step reads again.
      return [];
    } finally {
      if (inflight?.key === key && inflight.want === want) inflight = null;
    }
  })();
  inflight = { key, want, promise };
  return promise;
};

/** Sign-out (G1): nothing of the last person stays. */
export const forgetVisits = (): void => {
  kept = null;
  inflight = null;
  routeMissingUntil = 0;
};
